import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const CampaignSharePage = () => {
  const { businessId, campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [data, setData] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/impact-website/${businessId}`);
      setData(response.data);
      
      // Find the specific campaign
      const foundCampaign = response.data.campaigns?.find(c => c.campaign_id === campaignId);
      setCampaign(foundCampaign);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Campaign not found');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [businessId, campaignId]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareUrl = window.location.href;
  const primaryColor = data?.business?.primary_color || '#0C4E8C';
  const secondaryColor = data?.business?.secondary_color || '#C4A574';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-amber-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-amber-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Campaign Not Found</h1>
          <p className="text-gray-600">This campaign may no longer be available.</p>
        </div>
      </div>
    );
  }

  const progress = Math.min((campaign.raised_amount || 0) / (campaign.goal_amount || 1) * 100, 100);
  const impactValue = Math.round((campaign.raised_amount || 0) * (campaign.impact_conversion_rate || 1));
  const shareTitle = `Support ${campaign.title} - ${campaign.charity_name}`;
  const shareDescription = campaign.description || `Help us make a difference! Join ${data?.business?.name} in supporting ${campaign.charity_name}. Together we can create lasting impact.`;
  const shareImage = data?.business?.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(campaign.charity_name)}&size=600&background=0C4E8C&color=fff`;

  return (
    <>
      {/* Dynamic Meta Tags for Social Sharing */}
      <Helmet>
        <title>{shareTitle}</title>
        <meta name="description" content={shareDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={shareTitle} />
        <meta property="og:description" content={shareDescription} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:site_name" content={data?.business?.name || 'Altrunic'} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={shareUrl} />
        <meta name="twitter:title" content={shareTitle} />
        <meta name="twitter:description" content={shareDescription} />
        <meta name="twitter:image" content={shareImage} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-amber-50">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {data?.business?.logo_url ? (
                <img src={data.business.logo_url} alt="Logo" className="h-10 object-contain" />
              ) : (
                <span className="font-bold text-xl" style={{ color: primaryColor }}>
                  {data?.business?.name}
                </span>
              )}
            </div>
            <a 
              href={`/impact/${businessId}`}
              className="text-sm font-medium hover:underline"
              style={{ color: primaryColor }}
            >
              View All Campaigns →
            </a>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 py-8">
          {/* Share Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Hero Banner */}
            <div 
              className="relative h-48 flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
            >
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative text-center text-white px-6">
                <span className="inline-block px-4 py-1 bg-white/20 rounded-full text-sm font-medium mb-3">
                  🤝 Making a Difference Together
                </span>
                <h1 className="text-3xl font-bold">{campaign.title}</h1>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              {/* Charity Info */}
              <div className="flex items-center gap-4 mb-6">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold"
                  style={{ background: secondaryColor }}
                >
                  {campaign.charity_name?.charAt(0) || '?'}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{campaign.charity_name}</h2>
                  {campaign.charity_ein && (
                    <p className="text-sm text-gray-500">EIN: {campaign.charity_ein}</p>
                  )}
                </div>
              </div>

              {/* Description */}
              {campaign.description && (
                <p className="text-gray-600 leading-relaxed mb-6 text-lg">
                  {campaign.description}
                </p>
              )}

              {/* Progress Section */}
              <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                <div className="flex justify-between items-end mb-3">
                  <div>
                    <p className="text-3xl font-bold" style={{ color: primaryColor }}>
                      ${(campaign.raised_amount || 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">raised of ${(campaign.goal_amount || 0).toLocaleString()} goal</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">{Math.round(progress)}%</p>
                    <p className="text-sm text-gray-500">funded</p>
                  </div>
                </div>
                <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})` }}
                  />
                </div>
              </div>

              {/* Impact Stats */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <p className="text-2xl font-bold" style={{ color: primaryColor }}>{campaign.esg_score || '--'}</p>
                  <p className="text-sm text-gray-600">ESG Score</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <p className="text-2xl font-bold text-green-600">{impactValue > 0 ? impactValue.toLocaleString() : '--'}</p>
                  <p className="text-sm text-gray-600">{campaign.impact_metric || 'Lives Impacted'}</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-xl">
                  <p className="text-2xl font-bold" style={{ color: secondaryColor }}>
                    {campaign.employer_match_enabled ? `${campaign.employer_match_ratio || 1}x` : '—'}
                  </p>
                  <p className="text-sm text-gray-600">Employer Match</p>
                </div>
              </div>

              {/* CTA Button */}
              <a
                href={`/impact/${businessId}`}
                className="block w-full py-4 rounded-xl font-bold text-white text-center text-lg transition-all hover:opacity-90 hover:shadow-lg"
                style={{ background: primaryColor }}
              >
                💝 Donate Now
              </a>

              {/* Share Section */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-center text-gray-600 font-medium mb-4">
                  🌟 Help spread the word! Share this cause with your network
                </p>
                <div className="flex justify-center gap-3">
                  {/* Facebook */}
                  <a 
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(`🌟 I'm supporting ${campaign.title}! Join me in making a difference with ${data?.business?.name}. Every contribution counts! 💝`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-md"
                    style={{ background: '#1877F2' }}
                    title="Share on Facebook"
                  >
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  {/* Twitter/X */}
                  <a 
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`🌟 I'm supporting "${campaign.title}" by ${campaign.charity_name}!\n\n💪 Goal: $${(campaign.goal_amount || 0).toLocaleString()}\n📊 ESG Score: ${campaign.esg_score || 'N/A'}\n\nJoin me in making a difference! Every contribution creates real impact. 💝\n\n`)}&url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full flex items-center justify-center bg-black transition-all hover:scale-110 shadow-md"
                    title="Share on X"
                  >
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  {/* LinkedIn */}
                  <a 
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-md"
                    style={{ background: '#0A66C2' }}
                    title="Share on LinkedIn"
                  >
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                  {/* WhatsApp */}
                  <a 
                    href={`https://wa.me/?text=${encodeURIComponent(`🌟 Check out this amazing cause!\n\n"${campaign.title}" by ${campaign.charity_name}\n\n💝 Help make a difference today!\n\n${shareUrl}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-md"
                    style={{ background: '#25D366' }}
                    title="Share on WhatsApp"
                  >
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </a>
                  {/* Email */}
                  <a 
                    href={`mailto:?subject=${encodeURIComponent(`Join me in supporting ${campaign.title}! 🌟`)}&body=${encodeURIComponent(`Hi there!\n\nI wanted to share an amazing cause with you:\n\n🎯 Campaign: ${campaign.title}\n🏢 Organization: ${campaign.charity_name}\n💰 Goal: $${(campaign.goal_amount || 0).toLocaleString()}\n📊 ESG Score: ${campaign.esg_score || 'N/A'}\n\n${campaign.description || 'Help make a difference today!'}\n\n✨ Every contribution makes a real impact!\n\n👉 Learn more and donate: ${shareUrl}\n\nTogether, we can create lasting change! 💝\n\nBest regards`)}`}
                    className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-md"
                    style={{ background: secondaryColor }}
                    title="Share via Email"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                  </a>
                  {/* Copy Link */}
                  <button 
                    onClick={copyLink}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-md ${copied ? 'bg-green-500' : 'bg-gray-500 hover:bg-gray-600'}`}
                    title={copied ? 'Copied!' : 'Copy Link'}
                  >
                    {copied ? (
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 text-gray-500 text-sm">
            <p className="font-semibold" style={{ color: primaryColor }}>{data?.business?.name || 'Altrunic'}</p>
            <p className="mt-1">Making corporate giving simple and impactful</p>
          </div>
        </main>
      </div>
    </>
  );
};

export default CampaignSharePage;
